import * as React from "react"
import { Link, createFileRoute, useRouter } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { Archive, Edit3, Eye, Plus } from "lucide-react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { MemberShell } from "@/components/member-shell"
import { archivePropertyFn, getMyListingsFn } from "@/lib/app.functions"
import { formatPrice, titleCase } from "@/lib/constants"
import { requireMemberRoute } from "@/lib/route-guards"
import type { Property } from "@/lib/types"

export const Route = createFileRoute("/my-listings/")({
  loader: async () => {
    await requireMemberRoute("/my-listings")
    return getMyListingsFn()
  },
  head: () => ({
    meta: [{ title: "Owner listings · Harbor & Pine Realty" }],
  }),
  component: MyListingsPage,
})

function MyListingsPage() {
  const { user, listings } = Route.useLoaderData()
  return (
    <MemberShell
      user={user}
      eyebrow="Owner workspace · listing lifecycle"
      title="My listings"
      intro="Draft, publish, revise, preview, and archive fictional property records with a selectable demo media set."
      action={
        <Button
          className="field-button"
          nativeButton={false}
          render={<Link to="/my-listings/new" />}
        >
          <Plus />
          New listing
        </Button>
      }
    >
      {listings.length ? (
        <div className="space-y-4">
          {listings.map((property) => (
            <OwnerListing key={property.id} property={property} />
          ))}
        </div>
      ) : (
        <div className="border border-dashed bg-white p-8 text-center">
          <h2 className="font-heading text-3xl uppercase">
            No owner records yet
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Create a fictional listing to test the complete editor.
          </p>
        </div>
      )}
    </MemberShell>
  )
}

function OwnerListing({ property }: { property: Property }) {
  const archive = useServerFn(archivePropertyFn)
  const router = useRouter()
  const [pending, setPending] = React.useState(false)

  const handleArchive = async () => {
    setPending(true)
    try {
      await archive({ data: { id: property.id } })
      await router.invalidate()
      toast.success("Listing archived.")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to archive.")
    } finally {
      setPending(false)
    }
  }

  return (
    <article className="grid border bg-white md:grid-cols-[220px_minmax(0,1fr)_auto]">
      <div className="min-h-44 bg-muted">
        {property.images[0] ? (
          <img
            src={property.images[0].url}
            alt={property.images[0].alt}
            className="size-full object-cover"
          />
        ) : null}
      </div>
      <div className="min-w-0 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="field-label">{property.reference}</span>
          <Badge
            variant={property.status === "published" ? "default" : "outline"}
            className="rounded-none"
          >
            {titleCase(property.status)}
          </Badge>
        </div>
        <h2 className="mt-3 truncate font-heading text-3xl uppercase">
          {property.title}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {property.locality}, {property.region} ·{" "}
          {formatPrice(
            property.price,
            property.transaction,
            property.rentPeriod
          )}
        </p>
        <p className="mt-4 text-xs text-muted-foreground">
          Updated{" "}
          {new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(
            new Date(property.updatedAt)
          )}
        </p>
      </div>
      <div className="flex gap-2 border-t p-4 md:w-44 md:flex-col md:border-t-0 md:border-l">
        <Button
          variant="outline"
          className="field-button justify-start"
          nativeButton={false}
          render={
            <Link to="/property/$slug" params={{ slug: property.slug }} />
          }
        >
          <Eye />
          Preview
        </Button>
        <Button
          variant="outline"
          className="field-button justify-start"
          nativeButton={false}
          render={
            <Link
              to="/my-listings/$id/edit"
              params={{ id: String(property.id) }}
            />
          }
        >
          <Edit3 />
          Edit
        </Button>
        <Button
          variant="destructive"
          className="field-button justify-start"
          disabled={pending || property.status === "archived"}
          onClick={handleArchive}
        >
          <Archive />
          {pending ? "Archiving…" : "Archive"}
        </Button>
      </div>
    </article>
  )
}
